import React, { useState, useEffect } from 'react';
import { Building2, Save, Loader2, Mail, Phone, MapPin, Globe, Clock, CheckCircle, AlertCircle, XCircle, RefreshCw, Image } from 'lucide-react';
import ModernSettingHeader from '../../../components/settings/ModernSettingHeader';
import { toastMessage } from '@/utils/toast-message';
import Cookies from 'js-cookie';

interface ClientInfo {
  client_name: string;
  client_email: string;
  client_phone: string;
  notification_email: string;
  notification_email_verified?: boolean;
  notification_email_verified_at?: string | null;
  timezone: string;
  client_address: string;
  client_city: string;
  client_state: string;
  client_country: string;
  client_zip: string;
  client_website: string;
  client_description: string;
  client_image?: string;
  industry?: string;
}

interface VerificationStatus {
  verified: boolean;
  status: string;
  message: string;
  verifiedAt?: string | null;
}

const ClientInformationPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus | null>(null);
  const [clientInfo, setClientInfo] = useState<ClientInfo>({
    client_name: '',
    client_email: '',
    client_phone: '',
    notification_email: '',
    notification_email_verified: false,
    notification_email_verified_at: null,
    timezone: 'Asia/Kolkata',
    client_address: '',
    client_city: '',
    client_state: '',
    client_country: '',
    client_zip: '',
    client_website: '',
    client_description: '',
    client_image: '',
    industry: ''
  });

  // Industry options (same as signup page)
  const industries = [
    'Aerospace',
    'Advertising Agency',
    'Ecommerce',
    'Education',
    'Entertainment',
    'Fashion',
    'Finance',
    'FMCG',
    'Healthcare',
    'Hospitality',
    'Information Technology',
    'Manufacturing',
    'Oil & Gas',
    'Real Estate/Construction',
    'Retail',
    'Telecom',
    'Other',
  ];

  useEffect(() => {
    fetchClientInfo();
  }, []);

  const fetchClientInfo = async () => {
    try {
      setLoading(true);
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
      const token = Cookies.get('crm_auth_token');

      if (!token) {
        toastMessage('Authentication required', 'error');
        setLoading(false);
        return;
      }

      const response = await fetch(`${apiUrl}/clients/info`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch client information');
      }

      const data = await response.json();

      if (data.status === 'success' && data.data) {
        setClientInfo({
          client_name: data.data.client_name || '',
          client_email: data.data.client_email || '',
          client_phone: data.data.client_phone || '',
          notification_email: data.data.notification_email || '',
          notification_email_verified: data.data.notification_email_verified || false,
          notification_email_verified_at: data.data.notification_email_verified_at || null,
          timezone: data.data.timezone || 'Asia/Kolkata',
          client_address: data.data.client_address || '',
          client_city: data.data.client_city || '',
          client_state: data.data.client_state || '',
          client_country: data.data.client_country || '',
          client_zip: data.data.client_zip || '',
          client_website: data.data.client_website || '',
          client_description: data.data.client_description || '',
          client_image: data.data.client_image || '',
          industry: data.data.industry || ''
        });

        // Set verification status from client info data
        if (data.data.notification_email) {
          setVerificationStatus({
            verified: data.data.notification_email_verified || false,
            status: data.data.notification_email_verified ? 'Verified' : 'Not Verified',
            message: data.data.notification_email_verified ? 'Email is verified' : 'Email needs verification',
            verifiedAt: data.data.notification_email_verified_at || null
          });
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching client info:', error);
      toastMessage('Failed to load client information', 'error');
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ClientInfo, value: string) => {
    setClientInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
      const token = Cookies.get('crm_auth_token');

      if (!token) {
        toastMessage('Authentication required', 'error');
        setSaving(false);
        return;
      }

      const response = await fetch(`${apiUrl}/clients/info`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(clientInfo)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update client information');
      }

      const data = await response.json();

      if (data.status === 'success') {
        toastMessage('Client information updated successfully', 'success');
        // Refresh data to get updated timestamps
        await fetchClientInfo();
      } else {
        throw new Error(data.message || 'Failed to update client information');
      }
    } catch (error: any) {
      console.error('Error saving client info:', error);
      toastMessage(error.message || 'Failed to update client information', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!clientInfo.notification_email) {
      toastMessage('Please enter a notification email first', 'error');
      return;
    }

    try {
      setVerifying(true);
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
      const token = Cookies.get('crm_auth_token');

      if (!token) {
        toastMessage('Authentication required', 'error');
        setVerifying(false);
        return;
      }

      const response = await fetch(`${apiUrl}/clients/verify-notification-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to initiate email verification');
      }

      const data = await response.json();

      if (data.status === 'success') {
        toastMessage('Verification email sent! Please check your inbox.', 'success');
        // Check status after 2 seconds
        setTimeout(() => checkVerificationStatus(), 2000);
      } else {
        throw new Error(data.message || 'Failed to initiate email verification');
      }
    } catch (error: any) {
      console.error('Error verifying email:', error);
      toastMessage(error.message || 'Failed to send verification email', 'error');
    } finally {
      setVerifying(false);
    }
  };

  const checkVerificationStatus = async () => {
    if (!clientInfo.notification_email) {
      return;
    }

    try {
      setCheckingStatus(true);
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
      const token = Cookies.get('crm_auth_token');

      if (!token) {
        setCheckingStatus(false);
        return;
      }

      const response = await fetch(`${apiUrl}/clients/notification-email-status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to check verification status');
      }

      const data = await response.json();

      if (data.status === 'success' && data.data) {
        setVerificationStatus({
          verified: data.data.verified,
          status: data.data.status,
          message: data.data.message,
          verifiedAt: data.data.verifiedAt
        });

        // Update client info if verified
        if (data.data.verified) {
          setClientInfo(prev => ({
            ...prev,
            notification_email_verified: true,
            notification_email_verified_at: data.data.verifiedAt
          }));
        }
      }
    } catch (error: any) {
      console.error('Error checking verification status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full">
        <ModernSettingHeader
          title="Client Information"
          description="Manage your organization's information and contact details"
          icon={<Building2 className="h-5 w-5" />}
        />
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <ModernSettingHeader
        title="Client Information"
        description="Manage your organization's information and contact details"
        icon={<Building2 className="h-5 w-5" />}
      />

      <div className="p-6">
        <div className="max-w-4xl space-y-6">
          {/* Company Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Building2 className="w-5 h-5 mr-2 text-brandColor" />
              Company Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={clientInfo.client_name}
                  onChange={(e) => handleInputChange('client_name', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Enter company name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Website
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="url"
                    value={clientInfo.client_website}
                    onChange={(e) => handleInputChange('client_website', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="https://example.com"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Industry *
                </label>
                <select
                  value={clientInfo.industry}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select your industry</option>
                  {industries.map((industry) => (
                    <option key={industry} value={industry}>
                      {industry}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Timezone
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    value={clientInfo.timezone}
                    onChange={(e) => handleInputChange('timezone', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                    <option value="America/New_York">America/New_York (EST)</option>
                    <option value="America/Chicago">America/Chicago (CST)</option>
                    <option value="America/Denver">America/Denver (MST)</option>
                    <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
                    <option value="Europe/London">Europe/London (GMT)</option>
                    <option value="Europe/Paris">Europe/Paris (CET)</option>
                    <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                    <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
                    <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                    <option value="Australia/Sydney">Australia/Sydney (AEDT)</option>
                  </select>
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={clientInfo.client_description}
                  onChange={(e) => handleInputChange('client_description', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Brief description of your company"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Company Logo URL
                </label>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Image className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="url"
                        value={clientInfo.client_image}
                        onChange={(e) => handleInputChange('client_image', e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="https://example.com/logo.png"
                      />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Enter the URL of your company logo</p>
                  </div>
                  {clientInfo.client_image && (
                    <div className="w-24 h-24 border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-700 flex items-center justify-center">
                      <img
                        src={clientInfo.client_image}
                        alt="Logo preview"
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.parentElement!.innerHTML = '<span class="text-xs text-gray-400">Invalid URL</span>';
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Mail className="w-5 h-5 mr-2 text-brandColor" />
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    value={clientInfo.client_email}
                    onChange={(e) => handleInputChange('client_email', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="contact@example.com"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="tel"
                    value={clientInfo.client_phone}
                    onChange={(e) => handleInputChange('client_phone', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notification Email
                </label>
                <div className="space-y-3">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      value={clientInfo.notification_email}
                      onChange={(e) => handleInputChange('notification_email', e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="notifications@example.com"
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Email for system notifications and alerts (requires AWS SES verification)</p>

                  {/* Email Verification Status */}
                  {clientInfo.notification_email && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {verificationStatus?.verified ? (
                            <>
                              <CheckCircle className="w-5 h-5 text-green-600" />
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">Email Verified</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {verificationStatus.verifiedAt && new Date(verificationStatus.verifiedAt).toLocaleString()}
                                </p>
                              </div>
                            </>
                          ) : verificationStatus?.status === 'Pending' ? (
                            <>
                              <AlertCircle className="w-5 h-5 text-yellow-600" />
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">Verification Pending</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Check your inbox for verification link</p>
                              </div>
                            </>
                          ) : (
                            <>
                              <XCircle className="w-5 h-5 text-red-600" />
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">Not Verified</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Click verify to send verification email</p>
                              </div>
                            </>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {!verificationStatus?.verified && (
                            <button
                              onClick={handleVerifyEmail}
                              disabled={verifying || !clientInfo.notification_email}
                              className="inline-flex items-center px-3 py-1.5 text-sm bg-brandColor text-white rounded-md hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {verifying ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <Mail className="w-4 h-4 mr-1" />
                                  Verify Email
                                </>
                              )}
                            </button>
                          )}
                          <button
                            onClick={checkVerificationStatus}
                            disabled={checkingStatus || !clientInfo.notification_email}
                            className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {checkingStatus ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="mt-3 text-xs text-gray-600 dark:text-gray-400 bg-brandColor/10 dark:bg-brandColor/20 p-3 rounded border border-brandColor/30 dark:border-brandColor/40">
                        <p className="font-medium text-brandColor dark:text-brandColor mb-1">📧 Email Verification Required</p>
                        <p>Amazon SES requires email verification before you can send notifications. Click "Verify Email" to receive a verification link in your inbox. After clicking the link, use the refresh button to update the status.</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <MapPin className="w-5 h-5 mr-2 text-brandColor" />
              Address Information
            </h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Street Address
                </label>
                <input
                  type="text"
                  value={clientInfo.client_address}
                  onChange={(e) => handleInputChange('client_address', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="123 Business Street"
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={clientInfo.client_city}
                    onChange={(e) => handleInputChange('client_city', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="New York"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    value={clientInfo.client_state}
                    onChange={(e) => handleInputChange('client_state', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="NY"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    value={clientInfo.client_zip}
                    onChange={(e) => handleInputChange('client_zip', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="10001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Country
                  </label>
                  <select
                    value={clientInfo.client_country}
                    onChange={(e) => handleInputChange('client_country', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select country</option>
                    <option value="India">🇮🇳 India</option>
                    <option value="United Arab Emirates">🇦🇪 United Arab Emirates</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center px-6 py-3 bg-brandColor text-white font-medium rounded-lg hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientInformationPage;
